import React, { useState } from "react";
import { Plus, ShieldCheck, SquareTerminal, X } from "lucide-react";

import { AppLogo } from "@/shared/components/AppLogo";
import { Counted, ListCard, ListEmpty, ListRow, ListTile } from "@/shared/components/ListCard";
import { Chip, GhostButton, IconButton } from "@/shared/components/ui-kit";
import AddItemsDialog from "./AddItemsDialog";
import useApprovalOptions, { type ApprovalOption } from "./hooks/useApprovalOptions";

import type { Agent } from "@/shared/types";

/** A destructive action says so in the picker; a read says nothing. */
const RISK: Partial<Record<NonNullable<ApprovalOption["risk"]>, string>> = { danger: "Destructive", write: "Writes" };

const iconOf = (option: ApprovalOption | undefined, size: "row" | "picker") =>
  option?.logo !== undefined ? (
    size === "row"
      ? <AppLogo logo={option.logo} name={option.label} size={32} iconSize={18} rounded={8} />
      : <AppLogo logo={option.logo} name={option.label} size={20} iconSize={13} rounded={5} />
  ) : size === "row" ? (
    <ListTile><SquareTerminal className="w-4 h-4" strokeWidth={1.75} /></ListTile>
  ) : (
    <span className="w-5 h-5 shrink-0 rounded-[5px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
      <SquareTerminal className="w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />
    </span>
  );

/* The tools and app actions a person approves before each call. A draft field
   like any other: every change goes to the draft at once, and runs see it once
   it is published. A name the agent no longer has (its app unlinked, its switch
   off) is marked here, because publish refuses it (3060009). */
export function NeedsApproval({ agent, onChange }: { agent: Agent; onChange: (names: string[]) => void }) {
  const chosen = agent.needsApproval ?? [];
  const { options, loading } = useApprovalOptions(agent);
  const [adding, setAdding] = useState(false);
  const byName = new Map(options.map((option) => [option.name, option]));

  const items = options
    .filter((option) => !chosen.includes(option.name))
    .map((option) => ({
      id: option.name,
      icon: iconOf(option, "picker"),
      label: option.label,
      desc: option.label === option.name ? option.desc : `${option.name} · ${option.desc}`,
      right: option.risk && RISK[option.risk] ? <Chip tone={option.risk === "danger" ? "warn" : "neutral"}>{RISK[option.risk]}</Chip> : null,
    }));

  return (
    <>
      <ListCard
        label={<Counted n={chosen.length} noun="tool or action" plural="tools and actions" />}
        action={
          <GhostButton
            onClick={() => setAdding(true)}
            disabled={items.length === 0}
            title={items.length === 0 ? (loading ? "Reading this agent's tools…" : "Turn on apps or the sandbox to have tools to approve") : undefined}
            className="h-7 px-2.5 disabled:opacity-40 disabled:pointer-events-none"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add tools or actions
          </GhostButton>
        }
      >
        {chosen.length === 0 && (
          <ListEmpty icon={ShieldCheck} title="Nothing waits for approval" hint="Every call runs as the model asks. Add a tool or an app action to have a person approve it first." />
        )}
        {chosen.map((name) => {
          const option = byName.get(name);
          return (
            <ListRow
              key={name}
              lead={iconOf(option, "row")}
              title={option?.label ?? name}
              desc={option && option.label !== name ? <span className="font-mono text-[11.5px]">{name}</span> : undefined}
            >
              {!option && !loading && <Chip tone="warn">Not on this agent</Chip>}
              <IconButton
                onClick={() => onChange(chosen.filter((n) => n !== name))}
                title={`Stop asking before ${option?.label ?? name}`}
                aria-label={`Remove ${name}`}
                className="hover:text-[var(--danger)]"
              >
                <X className="w-3 h-3" strokeWidth={1.75} />
              </IconButton>
            </ListRow>
          );
        })}
      </ListCard>
      <AddItemsDialog
        open={adding}
        onOpenChange={setAdding}
        title="Needs approval"
        subtitle="A run stops before each of these and waits for someone to approve or deny it."
        noun="tool"
        items={items}
        onAdd={(names) => onChange([...chosen, ...names])}
      />
    </>
  );
}
